import React, { useState } from 'react';
import { Input } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import {
    TypesOfConsiderationRow,
    getConsiderationRow,
    HasConsiderationRows,
} from '../../../../slices/marketplace/loi-comparison/models/types-of-consideration-row.model';
import {
    TypeOfConsideration,
    TypesOfConsideration,
    isHeaderOnlyConsiderationType,
} from '../../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import {
    ConsiderationTreatment,
    ConsiderationTreatments,
    getAllowedConsiderationTreatments,
    withLegacyTreatment,
} from '../../../../slices/marketplace/pricing/models/consideration-treatment.enum';

export const CONSIDERATION_TABLE_ROW_ORDER: { type: TypeOfConsideration; indented?: boolean }[] = [
    { type: TypeOfConsideration.CashAtClose },
    { type: TypeOfConsideration.DeferredFixedPayments },
    { type: TypeOfConsideration.DeferredVariablePayments },
    { type: TypeOfConsideration.Earnout, indented: true },
    { type: TypeOfConsideration.SyntheticJvEquity, indented: true },
    { type: TypeOfConsideration.JvEquity },
    { type: TypeOfConsideration.CorporateEquity },
    { type: TypeOfConsideration.CommonEquity, indented: true },
    { type: TypeOfConsideration.PreferredEquity, indented: true },
    { type: TypeOfConsideration.Other },
];

export const getConsiderationTypeName = (considerationType: TypeOfConsideration): string =>
    TypesOfConsideration.find((item) => item.id === considerationType)?.name ?? '';

const parseCurrencyValue = (value: string | undefined): number | null =>
    value === undefined || value === '' ? null : Math.round(Number(value));

export interface ConsiderationTableItem extends HasConsiderationRows {
    buyerId: number;
    buyerName: string;
    fairValueByType?: Partial<Record<TypeOfConsideration, number | null>>;
    faceValueByType?: Partial<Record<TypeOfConsideration, number | null>>;
}

const getMirrorValue = (
    item: ConsiderationTableItem,
    type: TypeOfConsideration,
    feePaymentTiming: ConsiderationTreatment | null
): number | null | undefined => {
    if (feePaymentTiming === ConsiderationTreatment.FaceValueAtClosing) {
        return item.faceValueByType?.[type];
    }
    if (feePaymentTiming === ConsiderationTreatment.FairValueAtClosing) {
        return item.fairValueByType?.[type];
    }
    return undefined;
};

export interface TypesOfConsiderationTableProps {
    items: ConsiderationTableItem[];
    canEditTiming: boolean;
    canEditCashFlow: boolean;
    canEditComment: boolean;
    fullWidth?: boolean;
    levelOfService: number;
    onTimingChange: (buyerId: number, considerationType: TypeOfConsideration, patch: Partial<TypesOfConsiderationRow>) => void;
    onCashFlowChange: (buyerId: number, considerationType: TypeOfConsideration, projectedCashFlow: number | null) => void;
    onCommentChange: (buyerId: number, considerationType: TypeOfConsideration, comment: string | null) => void;
    onBlurSave: (buyerId: number) => void;
}

const EMPTY_ROW: TypesOfConsiderationRow = {
    considerationType: TypeOfConsideration.CashAtClose,
    feePaymentTiming: null,
    configuredTreatment: null,
    projectedCashFlow: null,
    comment: null,
    isActive: false,
};

const CHOICE_ANSWER_TREATMENTS = ConsiderationTreatments.filter(
    (treatment) =>
        treatment.id === ConsiderationTreatment.FairValueAtClosing ||
        treatment.id === ConsiderationTreatment.CashValueAtReceipt
);

const getChoiceAnswerOptions = (
    feePaymentTiming: ConsiderationTreatment | null,
    allowedTreatments: typeof ConsiderationTreatments
) => {
    const allowedChoiceAnswers = CHOICE_ANSWER_TREATMENTS.filter((treatment) =>
        allowedTreatments.some((allowed) => allowed.id === treatment.id)
    );
    return withLegacyTreatment(allowedChoiceAnswers, feePaymentTiming);
};

const LABEL_COLUMN_WIDTH = 209;
const SEPARATOR_COLUMN_WIDTH = 33;
const BUYER_GROUP_WIDTH = 720;

export const TypesOfConsiderationTable = ({
    items,
    canEditTiming,
    canEditCashFlow,
    canEditComment,
    fullWidth = false,
    levelOfService,
    onTimingChange,
    onCashFlowChange,
    onCommentChange,
    onBlurSave,
}: TypesOfConsiderationTableProps) => {
    const [cashFlowDrafts, setCashFlowDrafts] = useState<Record<string, string>>({});

    const allowedTreatments = getAllowedConsiderationTreatments(levelOfService);

    const tableWidth =
        LABEL_COLUMN_WIDTH + items.length * (BUYER_GROUP_WIDTH + SEPARATOR_COLUMN_WIDTH);

    const visibleRowOrder = CONSIDERATION_TABLE_ROW_ORDER.filter(({ type }) =>
        items.some((item) => getConsiderationRow(item, type)?.isActive)
    );

    return (
        <div className={`types-of-consideration-table${fullWidth ? ' types-of-consideration-table--full' : ''}`}>
            <table style={{ width: fullWidth ? '100%' : tableWidth }}>
                <thead>
                    <tr className="sub-col-header-row">
                        <th className="col-label section-title types-of-consideration-table__label-cell">
                            Types of Consideration
                        </th>
                        {items.map((item, index) => (
                            <React.Fragment key={item.buyerId}>
                                {index > 0 && (
                                    <th className="types-of-consideration-table__separator-cell" aria-hidden="true" />
                                )}
                                <th className="sub-col-header types-of-consideration-table__timing-col">
                                    Fee Payment Timing
                                </th>
                                <th className="sub-col-header types-of-consideration-table__cash-flow-col">
                                    Projected Cash Flow
                                </th>
                                <th className="sub-col-header types-of-consideration-table__comment-col">
                                    Comment
                                </th>
                            </React.Fragment>
                        ))}
                        <th className="types-of-consideration-table__separator-cell" aria-hidden="true" />
                    </tr>
                </thead>
                <tbody>
                    {visibleRowOrder.map(({ type, indented }) => (
                        <tr key={type} className="types-of-consideration-table__row">
                            <td
                                className={`col-label${indented ? ' col-label--indented' : ''} types-of-consideration-table__label-cell`}>
                                {getConsiderationTypeName(type)}
                            </td>
                            {isHeaderOnlyConsiderationType(type)
                                ? items.map((item, index) => (
                                    <React.Fragment key={item.buyerId}>
                                        {index > 0 && (
                                            <td className="types-of-consideration-table__separator-cell" aria-hidden="true" />
                                        )}
                                        <td />
                                        <td />
                                        <td />
                                    </React.Fragment>
                                ))
                                : items.map((item, index) => {
                                const row =
                                    getConsiderationRow(item, type) ?? { ...EMPTY_ROW, considerationType: type };
                                const isRowActive = row.isActive;
                                const isChoiceRow = row.configuredTreatment === ConsiderationTreatment.Choice;
                                const isCommentEditable = isRowActive && canEditComment;
                                const isCashFlowEditable = isRowActive && canEditCashFlow;
                                const isTimingEditable = isRowActive && canEditTiming && isChoiceRow;
                                const isChoiceUnanswered =
                                    row.feePaymentTiming == null || row.feePaymentTiming === ConsiderationTreatment.Choice;
                                const showTimingPlaceholder = isChoiceRow
                                    ? isChoiceUnanswered
                                    : row.feePaymentTiming == null;
                                const timingSelectValue = isChoiceRow && isChoiceUnanswered
                                    ? ''
                                    : row.feePaymentTiming ?? '';
                                const timingOptions = isChoiceRow
                                    ? getChoiceAnswerOptions(row.feePaymentTiming, allowedTreatments)
                                    : ConsiderationTreatments;
                                const draftKey = `${item.buyerId}-${type}`;
                                const ariaPrefix = `${item.buyerName} - ${getConsiderationTypeName(type)}`;
                                const isMirrorRow =
                                    row.feePaymentTiming === ConsiderationTreatment.FaceValueAtClosing ||
                                    row.feePaymentTiming === ConsiderationTreatment.FairValueAtClosing;
                                const mirroredValue = getMirrorValue(item, type, row.feePaymentTiming);
                                const cashFlowDisplayValue = isMirrorRow
                                    ? mirroredValue != null
                                        ? String(mirroredValue)
                                        : ''
                                    : cashFlowDrafts[draftKey] ??
                                        (row.projectedCashFlow != null ? String(row.projectedCashFlow) : '');

                                return (
                                    <React.Fragment key={item.buyerId}>
                                        {index > 0 && (
                                            <td className="types-of-consideration-table__separator-cell" aria-hidden="true" />
                                        )}
                                        <td>
                                            <Input
                                                type="select"
                                                value={timingSelectValue}
                                                disabled={!isTimingEditable}
                                                onChange={(event) => {
                                                    const selectedTreatment = Number(event.target.value) as ConsiderationTreatment;
                                                    const mirrorValue = getMirrorValue(item, type, selectedTreatment);
                                                    const mirrorPatch: Partial<TypesOfConsiderationRow> =
                                                        selectedTreatment === ConsiderationTreatment.FaceValueAtClosing ||
                                                        selectedTreatment === ConsiderationTreatment.FairValueAtClosing
                                                            ? { projectedCashFlow: mirrorValue ?? null }
                                                            : {};
                                                    onTimingChange(item.buyerId, type, {
                                                        feePaymentTiming: selectedTreatment,
                                                        ...mirrorPatch,
                                                    });
                                                }}
                                                aria-label={`${ariaPrefix} - Fee Payment Timing`}>
                                                {showTimingPlaceholder && (
                                                    <option value="" disabled>Select...</option>
                                                )}
                                                {timingOptions.map((treatment) => (
                                                    <option key={treatment.id} value={treatment.id} title={treatment.name}>
                                                        {treatment.name}
                                                    </option>
                                                ))}
                                            </Input>
                                        </td>
                                        <td>
                                            <CurrencyInput
                                                value={cashFlowDisplayValue}
                                                allowDecimals={false}
                                                allowNegativeValue={false}
                                                prefix="$"
                                                placeholder="$0"
                                                disabled={isMirrorRow || !isCashFlowEditable}
                                                onValueChange={(value) => {
                                                    if (isMirrorRow) {
                                                        return;
                                                    }
                                                    setCashFlowDrafts((prev) => ({ ...prev, [draftKey]: value ?? '' }));
                                                    onCashFlowChange(item.buyerId, type, parseCurrencyValue(value));
                                                }}
                                                onBlur={() => {
                                                    if (isMirrorRow) {
                                                        return;
                                                    }
                                                    setCashFlowDrafts((prev) => {
                                                        const next = { ...prev };
                                                        delete next[draftKey];
                                                        return next;
                                                    });
                                                    onBlurSave(item.buyerId);
                                                }}
                                                className="form-control"
                                                aria-label={`${ariaPrefix} - Projected Cash Flow`}
                                            />
                                        </td>
                                        <td className="types-of-consideration-table__comment-cell">
                                            <Input
                                                type="textarea"
                                                value={row.comment ?? ''}
                                                disabled={!isCommentEditable}
                                                onChange={(event) =>
                                                    onCommentChange(item.buyerId, type, event.target.value === '' ? null : event.target.value)
                                                }
                                                onBlur={() => onBlurSave(item.buyerId)}
                                                aria-label={`${ariaPrefix} - Comment`}
                                            />
                                        </td>
                                    </React.Fragment>
                                );
                            })}
                            <td className="types-of-consideration-table__separator-cell" aria-hidden="true" />
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
